import color from 'images/color.png';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import { getTranslate, Translate } from 'react-localize-redux';
import { TabContent, TabPane } from 'reactstrap';
import styled from 'styled-components';
import { useRecoilValue } from 'recoil';
import { AAdditionalColumns } from '../../atoms/additionalColumns';
import { AAdditionalFieldColumns } from '../../atoms/additionalFieldColumns';
import { ACalculatedFields } from '../../atoms/calculatedFields';
import { FREQUENCY_SCORE_STEPS, getColorByFrequencyScore, getEmojiByFrequencyScore } from '../../atoms/global/frequency';
import { UserLabel } from '../../components_v2/dropdown/style/Style';
import { labelPin } from '../../helpers/data/map';
import storeLang from '../../helpers/storeLang';
import { Dot } from '../../styles/global/css/Dot';
import { DefaultInputBlock } from '../../styles/global/css/GlobalInput';
import { DefaultTextDiv } from '../../styles/global/css/GlobalText';
import { PrimaryColor, DarkGreySidely2, GreenSidely, RedSidely, SidelyBlack, SurfaceColor } from '../../styles/global/css/Utils';
import { CompanyStatus } from '../client-companies/model/Model';
import { Owner } from '../orders/model/Model';
import { FlexDiv } from '../products/style';
import { getCurrencyFormatter, getNumberFormatter } from '../reports/utils';
import { DEFAULT_MAP_PIN_COLOR } from './cluster';
import { ColumnDisplayOptions, MapColorsKind, ScaleRange, booleanColorsOf, cssGradient, defaultColorScale, formatDaysFromToday, mapColorsKindOf, scaleBucketLabel, scaleBuckets, scaleRange } from './colorScale';
import { FetchKey, MinMaxAdditionalColumn } from './MapView';
import { PinId } from './modalLeft/TabPins';

const LEGEND_TABS = new Set<FetchKey>(['status', 'forms', 'last', 'next', 'instore', 'per_users', 'urchin', 'orders', 'frequencies', 'checkouts']);

const LegendContainer = styled.details`
	position: absolute;
	left: 10px;
	bottom: 30px;
	max-width: 210px;
	z-index: 1;
	max-height: 45%;
	overflow: auto;
	border-radius: 10px;
	background-color: ${SurfaceColor};
	box-shadow: 0 2px 6px color-mix(in oklab, ${SidelyBlack} 10%, transparent);
	&[open] {
		padding-bottom: 12px;
	}
	& > summary {
		list-style: none;
		padding: 12px 1rem;
	}
	&[open] > summary {
		padding-bottom: 8px;
	}
	& > summary::-webkit-details-marker {
		display: none;
	}
	& > summary svg {
		transition: transform 0.15s;
	}
	&[open] > summary svg {
		transform: rotate(180deg);
	}
`;

const LegendTitle = styled.summary`
	position: sticky;
	top: 0;
	z-index: 1;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	margin: 0;
	background-color: ${SurfaceColor};
	color: ${SidelyBlack};
	font-size: 13px;
	font-weight: bold;
	cursor: pointer;
`;

function Legend(props: { title: React.ReactNode, children: React.ReactNode }) {
	return <LegendContainer open>
		<LegendTitle>
			{props.title}
			<ChevronDown size={14} />
		</LegendTitle>
		{props.children}
	</LegendContainer>;
}

const GradientLegend = styled.div`
	width: 180px;
`;

const RangeInput = styled(DefaultInputBlock)`
	height: 24px;
	width: 50px;
	padding: 0;
	text-align: center;
	font-size: 12px;
`;

const StatusLegendTitle = styled(DefaultTextDiv)`
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: nowrap;
	font-size: 12px;
	color: ${DarkGreySidely2};
`;

const ScaleBar = styled.div`
	height: 12px;
	border-radius: 6px;
	margin: 4px 0;
`;

// the two ends of the bar, apart even when their words fill the row
const ScaleBound = styled.span`
	font-size: 11px;
	color: ${DarkGreySidely2};
	min-width: 0;
	&:last-child {
		text-align: right;
	}
`;

type ColumnTab = { additionalColumn: number } | { additionalFieldColumn: number } | { calculatedFieldColumn: number };

/**
 * The legend of a column tab. For a numeric column, its colour scale over
 * the values on the map (or its fixed range): a bar for a gradient, one line
 * per slice for steps, and the historical white to green when nothing is
 * set. For a boolean column, its colour per value.
 */
function ColumnScaleLegend(props: { tabType: ColumnTab, minMaxAdditionalColumn: MinMaxAdditionalColumn[], minMaxCalculatedFields: MinMaxAdditionalColumn[] }) {
	const additionalColumns = useRecoilValue(AAdditionalColumns);
	const additionalFieldColumns = useRecoilValue(AAdditionalFieldColumns);
	const calculatedFields = useRecoilValue(ACalculatedFields);
	const { tabType } = props;

	let name: string | undefined;
	let options: ColumnDisplayOptions | undefined;
	let minMax: MinMaxAdditionalColumn | undefined;
	let type: string | undefined;
	if ('additionalColumn' in tabType) {
		const column = additionalColumns.find(ac => ac.id === tabType.additionalColumn);
		name = column?.name;
		options = column?.display_options;
		minMax = props.minMaxAdditionalColumn.find(mm => mm.id === tabType.additionalColumn.toString() && !mm.field);
		type = column?.type;
	} else if ('additionalFieldColumn' in tabType) {
		const column = additionalFieldColumns.find(afc => afc.field_id === tabType.additionalFieldColumn);
		name = column?.field_name;
		options = column?.display_options;
		minMax = props.minMaxAdditionalColumn.find(mm => mm.id === tabType.additionalFieldColumn.toString() && mm.field);
		type = column?.field_type;
	} else {
		const column = calculatedFields.find(cf => cf.id === tabType.calculatedFieldColumn);
		name = column?.field_name;
		options = column?.display_options;
		minMax = props.minMaxCalculatedFields.find(mm => mm.id === tabType.calculatedFieldColumn.toString());
		type = column && 'ReportColumn';
	}
	const kind: MapColorsKind | undefined = type ? mapColorsKindOf(type) : undefined;
	if (!kind) return null;

	const noData = <FlexDiv gap='20px' fontSize='12px'>
		<Dot color={DEFAULT_MAP_PIN_COLOR} size='8px' />
		<Translate id='no data' />
	</FlexDiv>;

	if (kind === 'boolean') {
		const colors = booleanColorsOf(options);
		return <Legend title={name}>
			<div className="px-3">
				{(['true', 'false'] as const).map(key => <FlexDiv gap='20px' key={key} fontSize='12px'>
					<Dot color={colors[key]} size='8px' />
					<Translate id={key} />
				</FlexDiv>)}
				{noData}
			</div>
		</Legend>;
	}

	const scale = options?.color_scale;
	const isMoney = minMax?.isMoney ?? false;
	// a date column's bounds are days from today, as the scale reads them
	const isDate = type === 'Date';
	const toUnit = (value: number) => isMoney ? value / 100 : value;
	let onMap: ScaleRange = { min: 0, max: 0 };
	if (minMax && minMax.min <= minMax.max) onMap = { min: toUnit(minMax.min), max: toUnit(minMax.max) };
	const shown = scale ?? defaultColorScale(isDate);
	const range = scaleRange(shown, onMap);
	const formatter = isMoney ? getCurrencyFormatter() : getNumberFormatter();
	const format = isDate ? formatDaysFromToday : (value: number) => formatter.format(value);

	return <Legend title={name}>
		<div className="px-3">
			{shown.kind === 'gradient'
				? <>
					<ScaleBar style={{ background: cssGradient(shown) }} />
					<FlexDiv justify='space-between' align='flex-start' gap='12px'>
						<ScaleBound>{format(range.min)}</ScaleBound>
						<ScaleBound>{format(range.max)}</ScaleBound>
					</FlexDiv>
				</>
				: scaleBuckets(shown, onMap).map((bucket, i) => <FlexDiv gap='20px' key={`ScaleBucket[${i}]`} fontSize='12px'>
					<Dot color={bucket.color} size='8px' />
					<div>{scaleBucketLabel(bucket, format, isDate)}</div>
				</FlexDiv>)}
			{noData}
		</div>
	</Legend>;
}

export default function PinsLegend(props: {
	tabType: FetchKey
	range: { firstRange: number, lastRange: number }
	setRange: (ranges: { firstRange: number, lastRange: number }) => void
	handleRange: (t: PinId) => void
	status: CompanyStatus[]
	owners: Array<Owner>
	minMaxAdditionalColumn: MinMaxAdditionalColumn[]
	minMaxCalculatedFields: MinMaxAdditionalColumn[]
}) {
	const { tabType, handleRange } = props;
	const [ranges, setRanges] = React.useState({});
	const translate = getTranslate(storeLang.getState().localize);

	React.useEffect(() => {
		let key: string | undefined;
		switch (tabType) {
			case 'forms':
				key = 'forms';
				break;
			case 'last':
				key = 'last';
				break;
			case 'next':
				key = 'next';
				break;
			case 'orders':
				key = 'orders';
				break;
		}
		if (key) {
			props.setRange({
				firstRange: ranges[key]?.first ?? 0,
				lastRange: ranges[key]?.last ?? 30
			});
		}
	}, [tabType]);

	if (typeof tabType === 'object') {
		return <ColumnScaleLegend tabType={tabType} minMaxAdditionalColumn={props.minMaxAdditionalColumn} minMaxCalculatedFields={props.minMaxCalculatedFields} />;
	}
	if (!LEGEND_TABS.has(tabType)) return null;

	function getPinLegend(firstRange, lastRange, type) {
		return (
			(<GradientLegend>
				<img src={color} alt="" className="w-100 mb-2" />
				<div className="d-flex align-items-center justify-content-between mb-2">
					<div>
						<RangeInput
							name={firstRange}
							type="text"
							className="mr-1"
							defaultValue={props.range.firstRange}
							onChange={e => {
								props.setRange({
									...props.range,
									firstRange: parseInt(e.target.value, 10)
								});
								ranges[type] = { ...ranges[type], first: parseInt(e.target.value, 10) };
								setRanges({ ...ranges });
								handleRange(type);
							}}
						/>
						<small>
							<Translate id="general.days" />
						</small>
					</div>
					<div>
						<RangeInput
							name={lastRange}
							type="text"
							className="mr-1"
							defaultValue={props.range.lastRange}
							onChange={e => {
								props.setRange({
									...props.range,
									lastRange: parseInt(e.target.value, 10)
								});
								ranges[type] = { ...ranges[type], last: parseInt(e.target.value, 10) };
								setRanges({ ...ranges });
								handleRange(type);
							}}
						/>
						<small>
							<Translate id="general.days" />+
						</small>
					</div>
				</div>
			</GradientLegend>)
		);
	}

	function getLabel<T extends { color: string, label: string }>(labelPins: T[]) {
		return (
			<ul style={{ listStyle: 'none', paddingLeft: 0, marginBottom: 0 }}>
				{labelPins.map((val, i) => (
					<li className="d-flex align-items-center my-2" key={`labelPin[${i}]`}>
						<i
							className="mr-4 fas fa-circle"
							style={{ color: val.color, fontSize: '8px' }}
						/>
						<Translate id={val.label} />
					</li>
				))}
			</ul>
		);
	}

	return (
		<Legend title={<Translate id="Pins legend" />}>
			<TabContent activeTab={tabType}>
				<TabPane tabId={'status'}>
					<div className="px-3">
						{props.status.map((val, i) => <FlexDiv gap='20px' key={`DotPinStatus[${i}]`}>
							<Dot color={val.color_code} size='8px' />
							<StatusLegendTitle>{val.name}</StatusLegendTitle>
						</FlexDiv>
						)}
					</div>
				</TabPane>
				<TabPane tabId={'forms'} className="px-3">
					{getPinLegend('firsRangeOrder', 'LastRangeOrder', 'forms')}
					{getLabel([{ label: 'company_no_forms', color: '#83CEEE' }])}
				</TabPane>
				<TabPane tabId={'last'} className="px-3">
					{getPinLegend('firsRangeLast', 'LastRangeLast', 'last')}
					{getLabel(labelPin)}
				</TabPane>
				<TabPane tabId={'next'} className="px-3">
					{getPinLegend('firsRangeNext', 'LastRangeNext', 'next')}
					{getLabel([{ label: 'Companies with no planned events', color: '#83CEEE' }])}
				</TabPane>
				<TabPane tabId={'instore'}>
					<div className="px-3">
						{[
							{ color: GreenSidely, label: 'instore_presence' },
							{ color: '#C8ED34', label: 'order_in_progress' },
							{ color: '#FFEA2C', label: 'out_of_stock' },
							{ color: '#FF7669', label: 'de_listed' },
							{ color: '#FFA340', label: 'out_of_assortment' },
							{ color: PrimaryColor, label: 'other' },
							{ color: '#BABABA', label: 'uninformed' }
						].map((val, i) => <FlexDiv gap='20px' key={`DotPinInstore[${i}]`} fontSize='12px'>
							<Dot color={val.color} size='8px' />
							<Translate id={val.label} />
						</FlexDiv>)}
					</div>
				</TabPane>
				<TabPane tabId={'per_users'}>
					<div className="px-3">
						{props.owners.map((owner, i) => <FlexDiv gap='20px' key={`DotPinOwner[${i}]`}>
							<Dot color={owner.color} size='8px' />
							<UserLabel user={owner} me={translate('me').toString()}>{owner.name}</UserLabel>
						</FlexDiv>
						)}
					</div>
				</TabPane>
				<TabPane tabId={'urchin'}>
					<div className="px-3">
						{props.owners.map((owner, i) => <FlexDiv gap='20px' key={`DotPinOwner[${i}]`}>
							<Dot color={owner.color} size='8px' />
							<UserLabel user={owner} me={translate('me').toString()}>{owner.name}</UserLabel>
						</FlexDiv>
						)}
					</div>
				</TabPane>
				<TabPane tabId={'orders'} className="px-3">
					{getPinLegend('firsRangeOrder', 'LastRangeOrder', 'orders')}
					{getLabel([{ label: 'company_no_orders', color: '#83CEEE' }])}
				</TabPane>
				<TabPane tabId={'frequencies'}>
					<div className="px-3">
						<FlexDiv gap='20px' fontSize='12px'>
							<Dot color={DEFAULT_MAP_PIN_COLOR} size='8px' />
							<Translate id='no_frequency' />
						</FlexDiv>
						{FREQUENCY_SCORE_STEPS
							.reduce((acc: { score: number, lb: number, hb: number }[], score: number, i: number) => i == 0 ?
								acc :
								[...acc, { score: (score + FREQUENCY_SCORE_STEPS[i - 1]) / 2, lb: FREQUENCY_SCORE_STEPS[i - 1], hb: score }]
							, [])
							.map((score, i) => <FlexDiv gap='20px' key={`DotPinFrequencies[${i}]`} fontSize='12px'>
								<Dot color={getColorByFrequencyScore(score.score)} size='8px' />
								{getEmojiByFrequencyScore(score.score)}
								<div>
									{translate('from_{{NAME1}}_to_{{NAME2}}').toString().replace('{{NAME1}}', score.lb.toString()).replace('{{NAME2}}', score.hb == 7 ? '+∞' : score.hb.toString())}
								</div>
							</FlexDiv>
							)
						}
					</div>
				</TabPane>
				<TabPane tabId='checkouts'>
					<div className="px-3">
						<FlexDiv gap='20px' fontSize='12px'>
							<Dot color={GreenSidely} size='8px' />
							<Translate id='positive_evolution' />
						</FlexDiv>
						<FlexDiv gap='20px' fontSize='12px'>
							<Dot color={RedSidely} size='8px' />
							<Translate id='negative_evolution' />
						</FlexDiv>
						<FlexDiv gap='20px' fontSize='12px'>
							<Dot color={DEFAULT_MAP_PIN_COLOR} size='8px' />
							<Translate id='no_checkouts' />
						</FlexDiv>
					</div>
				</TabPane>
			</TabContent>
		</Legend>
	);
}
